/**
 * RenewalFields — shared renewal form fields
 *
 * Used by the project creation wizard (RenewalStep) and the inline
 * NewProjectModal on the invoice creation page. Keeps the two surfaces
 * in sync without duplicating UI logic.
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RECURRING_PERIODS } from '@/types/permits';
import type { PermitTypeDefinition } from '@/types/permits';

export const RENEWAL_REMINDER_OPTIONS = [
  { days: 7,  label: '7 days before' },
  { days: 14, label: '14 days before' },
  { days: 30, label: '30 days before' },
  { days: 60, label: '60 days before' },
  { days: 90, label: '90 days before' },
] as const;

export interface RenewalFieldsProps {
  permitTypeDefinitions: PermitTypeDefinition[];
  permitTypeDefinitionId: string;
  onPermitTypeDefinitionIdChange: (id: string) => void;
  expiryDate: string;
  onExpiryDateChange: (date: string) => void;
  recurringPeriod: string;
  onRecurringPeriodChange: (period: string) => void;
  reminderDays: number[];
  onReminderDaysChange: (days: number[]) => void;
}

export function RenewalFields({
  permitTypeDefinitions,
  permitTypeDefinitionId,
  onPermitTypeDefinitionIdChange,
  expiryDate,
  onExpiryDateChange,
  recurringPeriod,
  onRecurringPeriodChange,
  reminderDays,
  onReminderDaysChange,
}: RenewalFieldsProps) {
  const toggleReminderDay = (day: number) => {
    onReminderDaysChange(
      reminderDays.includes(day)
        ? reminderDays.filter(d => d !== day)
        : [...reminderDays, day]
    );
  };

  return (
    <div className="space-y-4" data-testid="renewal-fields">
      {/* Permit type — from API */}
      <div className="space-y-2">
        <Label>Permit Type</Label>
        <Select
          value={permitTypeDefinitionId}
          onValueChange={onPermitTypeDefinitionIdChange}
        >
          <SelectTrigger data-testid="permit-type-select">
            <SelectValue placeholder="Select permit type" />
          </SelectTrigger>
          <SelectContent>
            {permitTypeDefinitions.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expiry date — required */}
      <div className="space-y-2">
        <Label htmlFor="renewal-expiry-date">Expiry Date *</Label>
        <Input
          id="renewal-expiry-date"
          type="date"
          value={expiryDate}
          onChange={e => onExpiryDateChange(e.target.value)}
        />
      </div>

      {/* Recurring period */}
      <div className="space-y-2">
        <Label>Recurring Period</Label>
        <Select
          value={recurringPeriod}
          onValueChange={onRecurringPeriodChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select how often this renews" />
          </SelectTrigger>
          <SelectContent>
            {RECURRING_PERIODS.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reminder intervals */}
      <div className="space-y-2">
        <Label>Send reminders</Label>
        <div className="space-y-2">
          {RENEWAL_REMINDER_OPTIONS.map(({ days, label }) => (
            <div key={days} className="flex items-center gap-2">
              <Checkbox
                id={`renewal-reminder-${days}`}
                checked={reminderDays.includes(days)}
                onCheckedChange={() => toggleReminderDay(days)}
              />
              <Label
                htmlFor={`renewal-reminder-${days}`}
                className="font-normal cursor-pointer"
              >
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
